import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { ReduxState, ThunkDispatchType } from 'app/redux/types';

import { Icon, IconSize } from 'app/design-system/components/Icon';
import { SegmentedControl } from 'app/design-system/components/SegmentedControl';
import { useTranslation } from 'app/lang';
import useOutsideClickHook from 'app/lib/hooks/useOutsideClickHook';

import {
  SwitchSettingsSelect,
  switchSettingsSelect,
} from '../../../../actions/preferences';
import { AllowingPricePreference } from '../../../../constants';
import {
  selectSettingsActive,
  selectToAllowPricePreference,
} from '../../../../selectors/preferences';
import RefreshWarningToggle from '../../../Toggle/RefreshWarning';
import { SegmentedOption, useSegmentedOptions } from './useSegmentedOptions';

import dictionary from './Settings.dictionary.json';

import styles from './Settings.module.scss';

// ---
const DEFAULT_MENU_HEIGHT = 300;
const MENU_SHADOW_HEIGHT = 100;

type StoreProps = {
  settingsActive: boolean;
  allowPricePreference: AllowingPricePreference;
};

type DispatchProp = {
  dispatch: ThunkDispatchType<SwitchSettingsSelect>;
};

const Settings: FC<StoreProps & DispatchProp> = ({
  settingsActive,
  dispatch,
  allowPricePreference,
}) => {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [menuHeight, setMenuHeight] = useState(DEFAULT_MENU_HEIGHT);
  const segmentedOptions = useSegmentedOptions();
  const [selected, setSelected] = useState(allowPricePreference);

  useOutsideClickHook([buttonRef, containerRef], () => {
    dispatch(switchSettingsSelect(false));
  }, settingsActive);

  const closeSettingsHandler = useCallback(() => {
    dispatch(switchSettingsSelect(false));
  }, [settingsActive]);

  const switchSettingsSelectHandler = useCallback(() => {
    dispatch(switchSettingsSelect(!settingsActive));
  }, [settingsActive]);

  useEffect(() => {
    if (containerRef.current?.clientHeight) {
      setMenuHeight((containerRef.current.clientHeight + MENU_SHADOW_HEIGHT));
    }
  }, []);

  const handleSelectOption = useCallback((option: SegmentedOption) => {
    if (option.onSelect) {
      option.onSelect(option);
    }
    setSelected(option.id);
  }, []);

  return (
    <>
      <button type="button"
              ref={buttonRef}
              data-test="preferences-openButton"
              title={t(dictionary.preferencesTitle)}
              className={styles.settingsButton}
              data-ga-event="cart-settings"
              onClick={switchSettingsSelectHandler}>
        <Icon icon="preferences"
              size={16} />
      </button>

      <div className={classNames(styles.container, {
        [styles.expanded]: settingsActive,
      })}
           style={{ transform: `translateY(-${menuHeight}px)` }}
           data-test="preferences-container"
           ref={containerRef}>
        <button type="button"
                data-test="preferences-closeButton"
                className={styles.closeButton}
                onClick={closeSettingsHandler}>
          <Icon icon="close-tiny"
                size={IconSize.SMALL} />
        </button>
        <h1 className={styles.title}
            data-test="preferences-title">
          {t(dictionary.preferencesTitle)}
        </h1>

        <h2 className={styles.subtitle}
            data-test="preferences-subtitle">
          {t(dictionary.preferencesSubTitle)}
        </h2>
        <SegmentedControl options={segmentedOptions}
                          onSelect={handleSelectOption}
                          className={styles.menuPreferences}
                          selectedId={selected} />

        <RefreshWarningToggle className={styles.refreshToggle} />
        <p className={styles.toggleDescription}>
          {t(dictionary.toggleDescription)}
        </p>
      </div>
    </>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  settingsActive: selectSettingsActive(state),
  allowPricePreference: selectToAllowPricePreference(state),
});

export default connect(mapStateToProps)(Settings);
